import { DeploymentRequest } from '../types';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getTimestamp(d: any): Date | null {
  const raw = d.requestedAt || d.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Current consecutive-weekday streak of having at least one deployment
 * marked "deployed", counting backward from today (or the most recent
 * weekday if today is a weekend).
 */
export function computeDeploymentStreak(deployments: DeploymentRequest[]): number {
  const deployedDates = new Set(
    deployments
      .filter((d: any) => d.status === 'deployed')
      .map((d: any) => {
        const raw = d.deployment?.deployedAt || d.updatedAt || d.requestedAt;
        const date = raw ? new Date(raw) : null;
        return date && !isNaN(date.getTime()) ? date.toDateString() : null;
      })
      .filter(Boolean)
  );

  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  // Walk backward day by day, skipping weekends, until a weekday with no
  // deployment breaks the streak (today doesn't break it if it's just early).
  for (let i = 0; i < 365; i++) {
    const dayOfWeek = cursor.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      cursor.setDate(cursor.getDate() - 1);
      continue;
    }
    const isToday = i === 0;
    if (deployedDates.has(cursor.toDateString())) {
      streak++;
    } else if (!isToday) {
      break;
    }
    cursor.setDate(cursor.getDate() - 1);
  }

  return streak;
}

export interface PunchCardCell {
  day: number; // 0=Sun..6=Sat
  dayLabel: string;
  hour: number;
  count: number;
}

/** Day-of-week × hour-of-day density grid — how deployment activity clusters by time. */
export function computePunchCardData(deployments: DeploymentRequest[]): PunchCardCell[] {
  const grid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));

  deployments.forEach((d) => {
    const ts = getTimestamp(d);
    if (!ts) return;
    grid[ts.getDay()][ts.getHours()]++;
  });

  const cells: PunchCardCell[] = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      cells.push({ day, dayLabel: DAY_LABELS[day], hour, count: grid[day][hour] });
    }
  }
  return cells;
}

export type StripStatus = 'success' | 'fail' | 'neutral';

export interface StatusStripEntry {
  id: string;
  status: StripStatus;
  date: string;
  title: string;
}

/** Last N deployments in chronological order, classified for a status strip. */
export function computeStatusStrip(deployments: DeploymentRequest[], limit = 90): StatusStripEntry[] {
  const sorted = [...deployments]
    .filter((d: any) => getTimestamp(d))
    .sort((a: any, b: any) => getTimestamp(b)!.getTime() - getTimestamp(a)!.getTime())
    .slice(0, limit)
    .reverse();

  return sorted.map((d: any) => ({
    id: d.id || d._id,
    status: d.status === 'deployed' ? 'success' : d.status === 'rejected' ? 'fail' : 'neutral',
    date: getTimestamp(d)?.toLocaleDateString() || '',
    title: d.title || 'Untitled',
  }));
}
